/**
 * Property scope — which auto-collected keys are USER-level.
 *
 * Every SDK-controlled key that travels in `user_params` / `user_properties`
 * (device + locale, acquisition attribution, identity hints) must NOT also be
 * emitted inside the event body. Otherwise the same key shows up under both
 * "Event Properties" and "User Properties" in Event Analysis / Funnel filters
 * and the two sources produce different results (ANALYTICS-6725).
 *
 * Two layers use this list:
 *   1) the tracker, when it assembles an event (device / UTM / referrer spreads);
 *   2) the API senders, as a safety net right before `event_params` is built —
 *      covering dataLayer / platform trackers that inject the same keys.
 *
 * Developer-supplied custom properties are never in this list, so they are
 * untouched. `user_property` (the profile snapshot) is special: it is dropped
 * from every event except `user_identify`, whose payload it belongs to.
 */

export const USER_LEVEL_KEYS = new Set([
  // Locale / device — mirrored in user_params by buildUserParams()
  'language', 'timezone', 'timezone_offset', 'user_agent', 'platform',
  'os', 'os_version', 'browser', 'browser_version', 'device_type',
  'screen_resolution', 'color_depth', 'hardware_concurrency', 'device_memory',
  'is_touch_capable', 'webgl_vendor', 'canvas_hash',
  // Identity hints
  'app_identifier', 'fingerprint_id', 'device_class_hash', 'id_status',
  // Last-touch attribution (getAttributionUserParams)
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id',
  'referrer_domain', 'referring_url',
  // First-touch attribution
  'initial_referring_domain', 'initial_referring_url'
]);

/** Prefixes that are user-level as a family (initial_utm_source, …). */
export const USER_LEVEL_PREFIXES = ['initial_utm_'];

/** The profile snapshot key — allowed only on this event. */
export const USER_PROPERTY_KEY = 'user_property';
export const USER_PROPERTY_EVENT = 'user_identify';

/**
 * @param {string} key
 * @returns {boolean} true when the key is SDK-controlled user-level data
 */
export function isUserLevelKey(key) {
  if (typeof key !== 'string') return false;
  if (USER_LEVEL_KEYS.has(key)) return true;
  return USER_LEVEL_PREFIXES.some(prefix => key.startsWith(prefix));
}

/**
 * Should this key be dropped from the event body before it becomes an
 * event_param? Used by the senders as the final guard.
 * @param {string} key
 * @param {string} [eventName]
 * @returns {boolean}
 */
export function shouldDropEventParam(key, eventName) {
  if (isUserLevelKey(key)) return true;
  if (key === USER_PROPERTY_KEY) return eventName !== USER_PROPERTY_EVENT;
  return false;
}

/**
 * Shallow copy of `obj` without user-level keys. Click ids (gclid, fbclid, …)
 * and any developer key survive.
 * @param {Object} obj
 * @returns {Object}
 */
export function stripUserLevelKeys(obj) {
  const out = {};
  if (!obj || typeof obj !== 'object') return out;
  Object.keys(obj).forEach(key => {
    if (!isUserLevelKey(key)) out[key] = obj[key];
  });
  return out;
}
